import { useEffect } from "react"
import { isEnum } from "utils/isEnum"
import { parseEnum } from "utils/parseEnum"

import { Locale } from "@shared/enums/general"

import type { StorageSerializer } from "./storage"
import { useLocalState } from "./useLocalState"

/**
 * Local storage key to save the preferred locale
 */
export const USER_LOCALE_KEY = "userLocale"

/**
 * Determine a sensible default locale from the browser's own language
 * preferences. Dutch is preferred whenever it appears anywhere in the
 * browser's language list — English is only used as a last resort, when
 * Dutch isn't listed at all.
 */
export function detectBrowserLocale(): Locale {
  const languages =
    typeof navigator === "undefined" ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]

  const isDutch = languages.some((language) => language.toLowerCase().startsWith("nl"))
  return isDutch ? Locale.Nl : Locale.En
}

export let DEFAULT_LOCALE = detectBrowserLocale()

/**
 * For testing purposes: Adjust the default locale
 */
export function setDefaultLocale(locale: Locale): void {
  DEFAULT_LOCALE = locale
}

/**
 * `USER_LOCALE_KEY` is stored as a raw enum string (e.g. `nl-NL`), not JSON —
 * `AuthContext` writes it directly and `utils/translate.ts` reads it directly.
 * Use the same raw format here instead of the default JSON serializer.
 *
 * `from` also accepts the legacy JSON-quoted format (e.g. `"nl-NL"`) this hook
 * used to write via the default JSON serializer, so a locale a user picked
 * via `LanguageSwitch` before this fix still deserializes correctly instead
 * of silently reverting to the default locale.
 */
const rawLocaleSerializer: StorageSerializer<Locale> = {
  to: (value) => value,
  from: (value) => {
    if (isEnum(value, Locale)) {
      return value
    }

    try {
      return parseEnum(JSON.parse(value), Locale, DEFAULT_LOCALE)
    } catch {
      return DEFAULT_LOCALE
    }
  },
}

/**
 * Get access to the current locale of the website. Defaults to the
 * browser's language (see {@link detectBrowserLocale}).
 */
export function useLocale(): Locale {
  const [locale] = useLocalState(USER_LOCALE_KEY, DEFAULT_LOCALE, rawLocaleSerializer)

  return locale
}

/**
 * Hook to get both locale value and setter function. Defaults to the
 * browser's language (see {@link detectBrowserLocale}).
 */
export function useLocaleWithSetter(): { locale: Locale; setLocale: (locale: Locale) => void } {
  const [locale, setLocale] = useLocalState(USER_LOCALE_KEY, DEFAULT_LOCALE, rawLocaleSerializer)

  // Make sure google translate picks up the correct language
  useEffect(() => {
    const localePrefix = locale.substring(0, 2)
    document.documentElement.lang = localePrefix
    document.querySelector("meta[http-equiv='Content-Language']")?.setAttribute("content", localePrefix)
  }, [locale])

  return { locale, setLocale }
}
